/**
 * Central canonical event identity for exports, ledger rows and audits.
 *
 * Prefer: txHash#c{clause}:e{event}[:l{log}][:t{transfer}]
 * Do not use bare eventIndex alone — multi-clause txs share event indices.
 */

function toIntOrNull(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

function normalizeTxHash(txHash) {
  return String(txHash || "").trim().toLowerCase();
}

/**
 * @param {{
 *   txHash?: string,
 *   clauseIndex?: number|string|null,
 *   eventIndex?: number|string|null,
 *   logIndex?: number|string|null,
 *   transferIndex?: number|string|null,
 *   tokenContract?: string|null,
 *   classification?: string|null,
 *   direction?: string|null,
 *   seq?: number,
 *   kind?: string,
 * }} parts
 */
export function buildCanonicalEventKey(parts = {}) {
  const tx = normalizeTxHash(parts.txHash);
  const clause = toIntOrNull(parts.clauseIndex);
  const event = toIntOrNull(parts.eventIndex);
  const log = toIntOrNull(parts.logIndex);
  const transfer = toIntOrNull(parts.transferIndex);

  const base = tx || `synthetic:${parts.kind || "event"}:${parts.seq ?? 0}`;

  if (clause != null && event != null) {
    let key = `${base}#c${clause}:e${event}`;
    if (log != null && log !== event) key += `:l${log}`;
    if (transfer != null) key += `:t${transfer}`;
    return key;
  }

  if (event != null && clause == null) {
    // Incomplete identity — still better than bare digits when clause is missing.
    let key = `${base}#e${event}`;
    if (log != null && log !== event) key += `:l${log}`;
    if (transfer != null) key += `:t${transfer}`;
    return key;
  }

  if (clause != null && event == null) {
    let key = `${base}#c${clause}`;
    if (log != null) key += `:l${log}`;
    if (transfer != null) key += `:t${transfer}`;
    return key;
  }

  const cls = String(parts.classification || parts.kind || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (cls) return `${base}#${cls}`;

  if (parts.seq != null) return `${base}#${parts.kind || "event"}:${parts.seq}`;
  return base;
}

/**
 * Strip disposal/export suffixes so Form-8949 legs group under the parent event.
 * Examples: #gas_fee, #swap_out, #nft_mint, trailing :2 uniquify guards.
 */
export function normalizeParentDisposalEventKey(eventKey = "") {
  let key = String(eventKey || "").trim().toLowerCase();
  if (!key) return "";

  // Defensive uniquify suffix from export (not a real chain identity).
  key = key.replace(/:\d+$/, "");

  // Drop trailing classification suffixes after a structured clause/event identity.
  key = key.replace(
    /(#c\d+:e\d+(?::l\d+)?(?::t\d+)?)(?:\.(?:[a-z0-9_]+))?$/i,
    "$1"
  );

  // Legacy hash#<digits>.<class> or hash#<class>
  key = key.replace(
    /(#(?:c\d+:)?e?\d+(?::l\d+)?)(?:\.(gas_fee|swap_out|nft_mint|nft_galaxy_mint|sale_payment|mint_payment|[a-z0-9_]+))$/i,
    "$1"
  );

  key = key.replace(/#(gas_fee|swap_out|nft_mint|nft_galaxy_mint|sale_payment|mint_payment)$/i, "");

  return key;
}

/**
 * Build from a tax/export row using common field aliases.
 */
export function buildCanonicalEventKeyFromRow(row = {}, opts = {}) {
  const hash =
    row.hash ||
    row.txHash ||
    row.txid ||
    row.Tx_Hash ||
    row.disposal_hash ||
    opts.txHash ||
    "";

  const clause =
    row.clause_index ??
    row.clauseIndex ??
    row.raw_json?.clause_index ??
    row.raw_json?.clauseIndex ??
    opts.clauseIndex;

  const event =
    row.event_index ??
    row.eventIndex ??
    row.raw_json?.event_index ??
    row.raw_json?.eventIndex ??
    opts.eventIndex;

  const log =
    row.log_index ??
    row.logIndex ??
    row.raw_json?.log_index ??
    row.raw_json?.logIndex ??
    opts.logIndex;

  const transfer =
    row.transfer_index ??
    row.transferIndex ??
    opts.transferIndex;

  return buildCanonicalEventKey({
    txHash: hash,
    clauseIndex: clause,
    eventIndex: event,
    logIndex: log,
    transferIndex: transfer,
    tokenContract: row.token_contract || row.tokenContract || opts.tokenContract,
    classification: row.classification || row.type || opts.kind,
    seq: opts.seq,
    kind: opts.kind || "event",
  });
}
